import React from 'react';

/**
 * Bloco "Registro Fotográfico" do relatório clínico em PDF.
 *
 * Recebe a estrutura já pronta (um par antes/depois por vista) com as object
 * URLs resolvidas, e a paleta do template, para manter exatamente a mesma
 * tipografia e as mesmas cores do restante do relatório.
 *
 * Estilos inline, como o resto do template: o html2canvas não lê CSS modules.
 *
 * @param {object} photoReport { pairs: [{ view, viewLabel, before, current }] },
 *                 cada ponta { id, size, url, date } (before pode ser null)
 * @param {object} palette paleta do ClinicalReportTemplate
 */
const ClinicalReportPhotos = ({ photoReport, palette }) => {
    if (!photoReport) return null;

    const pairs = photoReport.pairs || [];

    if (pairs.length === 0) return null;

    const s = {
        pairBlock: {
            marginBottom: '12px',
            padding: '10px 12px',
            backgroundColor: palette.blueBg,
            borderRadius: '10px',
            border: `1px solid ${palette.bluePale}`,
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
        },
        pairView: {
            display: 'block',
            fontSize: '9px',
            fontWeight: 700,
            color: palette.navy,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '6px',
        },
        pairRow: {
            display: 'flex',
            gap: '12px',
        },
        pairItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            width: '200px',
        },
        pairBadge: {
            fontSize: '8px',
            fontWeight: 700,
            color: palette.blue,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        },
        pairImage: {
            width: '200px',
            height: '200px',
            objectFit: 'cover',
            borderRadius: '10px',
            border: `1px solid ${palette.bluePale}`,
            backgroundColor: palette.white,
        },
        pairPlaceholder: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '200px',
            height: '200px',
            borderRadius: '10px',
            border: `1px dashed ${palette.bluePale}`,
            backgroundColor: palette.white,
            fontSize: '9px',
            color: palette.textLight,
            textAlign: 'center',
        },
        pairDate: {
            fontSize: '9px',
            fontWeight: 600,
            color: palette.textMedium,
        },
    };

    const getTitle = (pair) => (pair.before
        ? `${pair.viewLabel} — Antes (${pair.before.date}) × Atual (${pair.current.date})`
        : `${pair.viewLabel} — Atual (${pair.current.date})`);

    const PairItem = ({ item, badge, viewLabel }) => (
        <div style={s.pairItem}>
            <span style={s.pairBadge}>{badge}</span>

            {item
                ? (item.url && <img src={item.url} alt={`${viewLabel} - ${item.date}`} style={s.pairImage} />)
                : <div style={s.pairPlaceholder}>Aguardando próxima medição</div>
            }

            <span style={s.pairDate}>{item ? item.date : ''}</span>
        </div>
    );

    return (
        <div>
            {pairs.map(pair => (
                <div key={pair.view || 'none'} style={s.pairBlock}>
                    <span style={s.pairView}>{getTitle(pair)}</span>

                    <div style={s.pairRow}>
                        <PairItem item={pair.before} badge="Antes" viewLabel={pair.viewLabel} />
                        <PairItem item={pair.current} badge="Atual" viewLabel={pair.viewLabel} />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ClinicalReportPhotos;
